import React from 'react'

function formatResponseValue(question, value) {
  if (question.type === 'boolean') return value ? 'Yes' : '—'
  if (question.type === 'select' && Array.isArray(question.options)) {
    const opt = question.options.find((o) => o.value === value)
    return opt?.label || value || '—'
  }
  return value != null && String(value).trim() !== '' ? String(value) : '—'
}

function SelectField({ label, value, onChange, options, disabled, quizStyle = false }) {
  if (quizStyle && options?.length) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">{label}</label>
        <div className="space-y-2">
          {options.map((opt) => {
            const selected = value === opt.value
            return (
              <button
                key={opt.value}
                type="button"
                disabled={disabled}
                onClick={() => onChange(opt.value)}
                className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
                  selected
                    ? 'border-psg-teal bg-psg-teal/5 text-gray-900 ring-1 ring-psg-teal'
                    : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>
    )
  }
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select className="select-field" value={value ?? ''} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        <option value="">Select</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  )
}

function QuestionField({ question, value, onChange, disabled, quizStyle = false }) {
  if (question.type === 'select' && Array.isArray(question.options)) {
    return (
      <SelectField
        label={question.label}
        value={value}
        onChange={onChange}
        disabled={disabled}
        options={question.options}
        quizStyle={quizStyle}
      />
    )
  }
  if (question.type === 'boolean') {
    return (
      <label className="flex items-center space-x-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4"
          checked={!!value}
          onChange={() => onChange(!value)}
          disabled={disabled}
        />
        <span>{question.label}</span>
      </label>
    )
  }
  if (question.type === 'number') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{question.label}</label>
        <input
          type="number"
          className="input-field"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    )
  }
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{question.label}</label>
      <input
        className="input-field"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </div>
  )
}

function ReportRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline py-1 border-b border-gray-100 last:border-0 text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900">{value || '—'}</span>
    </div>
  )
}

function ReportSection({ title, children, className = '' }) {
  return (
    <div className={`mb-3 ${className}`.trim()}>
      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">{title}</h4>
      <div className="bg-gray-50 rounded p-2.5 border border-gray-100">{children}</div>
    </div>
  )
}

export function VenueRiskAssessmentReport({ template, responses, dimensionScores = {} }) {
  const dimensions = (template?.dimensions || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0">
      {dimensions.map((dim) => (
        <ReportSection key={dim.id} title={dim.label} className={dim.questions?.length > 4 ? 'md:col-span-2' : ''}>
          {dim.questions?.map((q) => (
            <ReportRow key={q.id} label={q.label} value={formatResponseValue(q, responses?.[q.id])} />
          ))}
          {dimensionScores[dim.id] != null && (
            <div className="mt-2 text-xs text-gray-500">Dimension score: {dimensionScores[dim.id]}%</div>
          )}
        </ReportSection>
      ))}
    </div>
  )
}

export function VenueRiskAssessmentForm({ template, responses, onResponseChange, readOnly = false, quizStyle = true }) {
  if (!template?.dimensions) return <p className="text-sm text-gray-500">No assessment template loaded.</p>
  const dimensions = [...template.dimensions].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

  return (
    <div className="space-y-5">
      {dimensions.map((dim) => {
        const questions = [...(dim.questions || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        const booleans = questions.filter((q) => q.type === 'boolean')
        const others = questions.filter((q) => q.type !== 'boolean')
        return (
          <div key={dim.id} className="p-4 border rounded bg-white">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <h4 className="text-md font-semibold text-gray-900">{dim.label}</h4>
              {dim.weight != null && <span className="text-xs text-gray-400">Weight {dim.weight}%</span>}
            </div>
            {others.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                {others.map((q) => (
                  <QuestionField
                    key={q.id}
                    question={q}
                    value={responses?.[q.id]}
                    disabled={readOnly}
                    quizStyle={quizStyle}
                    onChange={(v) => onResponseChange(q.id, v)}
                  />
                ))}
              </div>
            )}
            {booleans.length > 0 && (
              <div className="space-y-2">
                {booleans.map((q) => (
                  <QuestionField
                    key={q.id}
                    question={q}
                    value={responses?.[q.id]}
                    disabled={readOnly}
                    onChange={(v) => onResponseChange(q.id, v)}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
